import { useI18n } from '~/common/context';
/* eslint-disable @wyz/module/no-relative-import */
import { CenteredSpinner } from '@wyz/ui';
import { mapIntoOptionItem } from '@wyz/utils';
import React, { useMemo } from 'react';
import { RIM_TYPE_LABEL_LOOKUP } from '~/common/translators';
import { RecordKeys } from '~/common/types';
import { RIM_TYPE_ENUM, TYRE_COUNT_ENUM } from '~/purchase/schemas';
import { IRimType, ITyreCountType } from '~/purchase/types';
import { FormContext, IFormContext } from './contexts';
import { IVehicleTypeGroupsOptionItem } from '~/common/types';
import { useActiveVehicleTypeGroups } from '~/purchase/hooks';

export function QuoteServicePricingPolicyFormProvider({
  children,
  mode,
}: React.PropsWithChildren<Pick<IFormContext, 'mode'>>) {
  const { t } = useI18n();

  const activeVehicleTypeGroups = useActiveVehicleTypeGroups();

  const context: IFormContext = useMemo(() => {
    return {
      mode,
      vehicleTypeGroups: {
        ...activeVehicleTypeGroups,
        optionItems: mapIntoOptionItem(
          activeVehicleTypeGroups.data?.data ?? [],
          (item) => t(item.i18nKey, { defaultValue: item.name }),
          'publicKey',
          (item) => ({ name: item.name }),
        ) as Array<IVehicleTypeGroupsOptionItem>,
      },
      rims: {
        optionItems: Object.keys(RIM_TYPE_ENUM).map((rimeTypeCodeName) => ({
          value: rimeTypeCodeName,
          label: t(RIM_TYPE_LABEL_LOOKUP[rimeTypeCodeName as IRimType]),
        })),
      },
      tyreCounts: {
        optionItems: Object.keys(TYRE_COUNT_ENUM).map((tyreCountCodeName) => ({
          value: tyreCountCodeName as ITyreCountType,
          label: t('app.quote.tyre_count', {
            count:
              TYRE_COUNT_ENUM[
                tyreCountCodeName as RecordKeys<typeof TYRE_COUNT_ENUM>
              ],
          }),
        })),
      },
    } satisfies IFormContext;
  }, [activeVehicleTypeGroups, mode, t]);

  const requiredQueries = [activeVehicleTypeGroups];
  return (
    <FormContext.Provider value={context}>
      {requiredQueries.some((query) => !query.isSuccess) && <CenteredSpinner />}
      {requiredQueries.every((query) => query.isSuccess) && children}
    </FormContext.Provider>
  );
}
export const useQuoteServicePricingPolicyForm = () => {
  const context = React.useContext(FormContext);
  if (context === undefined) {
    throw new Error(
      'useQuoteServicePricingPolicyForm must be used within a QuoteServicePricingPolicyFormProvider',
    );
  }
  return context;
};
